import { ArrowLeft, Target, Heart, Sparkles, Telescope, MapPin, Rocket } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';

export default function FutureVisionGuide() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-indigo-50 to-white">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6 text-indigo-700 hover:text-indigo-800 hover:bg-indigo-100"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Week
        </Button>

        <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-indigo-100 rounded-full">
              <Telescope className="w-8 h-8 text-indigo-600" />
            </div>
            <div>
              <h1 className="text-3xl font-serif font-bold text-gray-900">Future Vision</h1>
              <p className="text-indigo-600">Week 12, Day 4 - Keeping Your Glow</p>
            </div>
          </div>

          <div className="prose prose-indigo max-w-none">
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Set 3 specific goals for the next 3 months. For each goal, identify why it matters, 
              your first step, potential obstacles, and how you'll celebrate when you achieve it.
            </p>

            <div className="bg-indigo-50 rounded-xl p-6 mb-8">
              <h2 className="text-xl font-semibold text-indigo-800 mb-4 flex items-center gap-2">
                <Heart className="w-5 h-5" />
                Why New Goals Matter Now
              </h2>
              <ul className="space-y-2 text-gray-700">
                <li>• Maintains momentum from your 12-week journey</li>
                <li>• Prevents backsliding into old patterns</li>
                <li>• Gives continued purpose and direction</li>
                <li>• Applies everything you've learned to your next chapter</li>
                <li>• Keeps growth as a lifestyle, not just a program</li>
              </ul>
            </div>

            <h2 className="text-2xl font-serif font-bold text-gray-900 mb-4">Goal Setting Framework</h2>
            
            <p className="text-gray-700 mb-4">For each of your 3 goals, answer these questions:</p>
            
            <div className="space-y-4 mb-8">
              <div className="bg-white border-2 border-indigo-200 rounded-xl p-5">
                <h3 className="font-semibold text-indigo-700 mb-3">Goal #1</h3>
                <div className="space-y-3">
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-gray-700 font-medium">What's the goal? (Be specific)</p>
                    <p className="text-gray-500 text-sm italic mt-1">Example: "Exercise 4 times per week" not "Get fit"</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-gray-700 font-medium">Why does this matter to you?</p>
                    <p className="text-gray-500 text-sm italic mt-1">Connect to deeper values and feelings</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-gray-700 font-medium">What's ONE first step you can take this week?</p>
                    <p className="text-gray-500 text-sm italic mt-1">Make it small and achievable</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-gray-700 font-medium">What obstacles might arise? How will you handle them?</p>
                    <p className="text-gray-500 text-sm italic mt-1">Anticipate and plan for challenges</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-gray-700 font-medium">How will you celebrate when you achieve it?</p>
                    <p className="text-gray-500 text-sm italic mt-1">Plan a meaningful reward</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-indigo-100 to-purple-100 rounded-xl p-6 mb-8">
              <h2 className="text-xl font-semibold text-indigo-800 mb-4 flex items-center gap-2">
                <Target className="w-5 h-5" />
                Goal Ideas by Area
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Physical</h3>
                  <ul className="text-gray-600 text-sm space-y-1">
                    <li>• Consistent workout schedule</li>
                    <li>• Try a new physical activity</li>
                    <li>• Sleep goal achievement</li>
                    <li>• Nutrition improvement</li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Mental/Emotional</h3>
                  <ul className="text-gray-600 text-sm space-y-1">
                    <li>• Daily meditation practice</li>
                    <li>• Therapy or coaching</li>
                    <li>• Learn a new skill</li>
                    <li>• Read X books</li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Relationships</h3>
                  <ul className="text-gray-600 text-sm space-y-1">
                    <li>• Weekly quality time with partner</li>
                    <li>• Monthly friend dates</li>
                    <li>• Family connection routine</li>
                    <li>• Set better boundaries</li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Career/Purpose</h3>
                  <ul className="text-gray-600 text-sm space-y-1">
                    <li>• Complete a certification</li>
                    <li>• Start a side project</li>
                    <li>• Networking goal</li>
                    <li>• Have a career conversation</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-amber-50 rounded-xl p-6">
              <h2 className="text-xl font-semibold text-amber-800 mb-4 flex items-center gap-2">
                <Rocket className="w-5 h-5" />
                Tips for Success
              </h2>
              <ul className="space-y-2 text-gray-700">
                <li>• Write your goals somewhere you'll see them daily</li>
                <li>• Share them with someone who'll hold you accountable</li>
                <li>• Schedule your first steps in your calendar NOW</li>
                <li>• Review progress weekly</li>
                <li>• Be flexible - adjust as needed, but don't abandon</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
